// Bugsnag
import Bugsnag from '@bugsnag/js';
import Moveable, { OnDrag, OnResize } from 'moveable';
import { ComputedRef, nextTick, Ref, ref } from 'vue';

import GAnalytics from '@/analytics/ganalytics/utils/GAnalytics';
import { useDeviceInfo } from '@/common/composables/useDeviceInfo';
import { useMainStore } from '@/editor/stores/store';
import { DomNodes } from '@/elements/element/dom/DomNodes';
import { DomNodesMedia } from '@/elements/element/dom/DomNodesMedia';
import { TransformTools } from '@/elements/element/utils/TransformTools';
import { CroppeableElement } from '@/elements/medias/crop/types/croppeable.type';
import { MaskableElement } from '@/elements/medias/mask/types/maskable.type';
import { useInteractions } from '@/interactions/composables/useInteractions';
import { useSelection } from '@/interactions/composables/useSelection';
import { useProjectStore } from '@/project/stores/project';
import { EditPanels, Position, Size } from '@/Types/types';

const ghostMoveable = ref<Moveable | null>();

const initialSize = ref<Size>({ width: 0, height: 0 });
const initialPosition = ref<Position>({ x: 0, y: 0 });
const initialCropSize = ref<Size>({ width: 0, height: 0 });
const initialCropPosition = ref<Position>({ x: 0, y: 0 });
const initialDirections = ref<boolean | string[] | undefined>([]);
const fromApplyCrop = ref(false);
const imagePanelWasOpen = ref(false);

export const useCrop = (
	element: Ref<CroppeableElement | MaskableElement> | ComputedRef<CroppeableElement | MaskableElement>
) => {
	const store = useMainStore();
	const { selectionId, clearSelection } = useSelection();

	const project = useProjectStore();

	const temporalSize = ref<Size | null>(null);

	const { isMobile } = useDeviceInfo();
	const { moveable, isCropping } = useInteractions();
	const initCrop = async () => {
		project.pauseAutoSave?.();

		// Cerramos el panel de imágenes en caso de estar abierto
		if (store.editPanel === EditPanels.Image && !isMobile.value) {
			store.editPanel = null;
			imagePanelWasOpen.value = true;
		}
		// Guardamos los datos iniciales para restaurarlos en caso de cancelar el crop
		initialSize.value = {
			width: element.value.size.width,
			height: element.value.size.height,
		};

		initialPosition.value = {
			x: element.value.position.x,
			y: element.value.position.y,
		};

		initialCropSize.value = {
			width: element.value.crop.size.width,
			height: element.value.crop.size.height,
		};

		initialCropPosition.value = {
			x: element.value.crop.position.x,
			y: element.value.crop.position.y,
		};

		// Si no tiene crop le seteamos el tamaño de crop al original
		if (!element.value.hasCrop()) {
			element.value.crop.size = {
				width: element.value.size.width,
				height: element.value.size.height,
			};
		}

		await nextTick();
		setMoveableConfigForCrop();
		await nextTick();

		ghostMoveable.value = createGhostMoveable();
		registerGhostMoveableEvents();
		GAnalytics.track('click', 'Button', `crop`, null);
	};

	const stopCrop = () => {
		unsetMoveableConfigForCrop();
		store.croppingId = null;
		ghostMoveable.value?.destroy();
		ghostMoveable.value = null;
		project.resumeAutoSave?.();
	};

	const cancelCrop = () => {
		// Ignoramos el autosave al cancelar el crop, ya que recuperamos su estado previo
		project.ignoreAutoSave?.(() => {
			element.value.size = {
				width: initialSize.value.width,
				height: initialSize.value.height,
			};

			element.value.position = {
				x: initialPosition.value.x,
				y: initialPosition.value.y,
			};

			element.value.crop.size = {
				width: initialCropSize.value.width,
				height: initialCropSize.value.height,
			};

			element.value.crop.position = {
				x: initialCropPosition.value.x,
				y: initialCropPosition.value.y,
			};
		});
		Bugsnag.leaveBreadcrumb(`Cancel crop to ${element.value.id}`);

		stopCrop();
	};

	const applyCrop = async () => {
		if (!element.value) {
			throw new Error('Not element to crop');
		}
		Bugsnag.leaveBreadcrumb(`Apply crop to ${element.value.id}`);

		fromApplyCrop.value = true;

		await nextTick();
		stopCrop();
		project.saveState?.();
	};

	const setMoveableConfigForCrop = () => {
		if (!moveable.value) {
			throw new Error('Moveable error');
		}

		initialDirections.value = moveable.value.renderDirections;

		if ('mask' in element.value) {
			moveable.value!.keepRatio = element.value.mask?.keepRatio;
			moveable.value.renderDirections = element.value.mask?.keepRatio
				? ['nw', 'ne', 'sw', 'se']
				: ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];
		}
		moveable.value.draggable = false;
		moveable.value.rotationPosition = 'none';
		moveable.value.snappable = false;
		moveable.value.target = document.querySelector('.main-image') as HTMLElement;
		moveable.value?.updateTarget();
	};

	const createGhostMoveable = () => {
		return new Moveable(document.body, {
			target: document.querySelector('.ghost-image') as HTMLElement,
			renderDirections: ['nw', 'ne', 'sw', 'se'],
			snappable: false,
			draggable: true,
			resizable: true,
			rotatable: false,
			pinchable: false,
			origin: false,
			keepRatio: true,
			edge: false, // Resize, Scale Events at edges.
			rootContainer: document.getElementById('scroll-area'),
			container: document.getElementById('scroll-area'),
			portalContainer: document.getElementById('portalTargetCrop'),
			checkInput: true,
			className: 'moveable-ghost',
			// padding: {
			// 	left: 5,
			// 	top: 5,
			// 	right: 5,
			// 	bottom: 5,
			// },
		});
	};

	const registerGhostMoveableEvents = () => {
		ghostMoveable.value!.on('drag', dragHandler).on('resize', resizeHandler);
	};

	const dragHandler = (ev: OnDrag) => {
		const pos = { x: element.value.crop.position.x + ev.delta[0], y: element.value.crop.position.y + ev.delta[1] };
		element.value.crop.position = pos;
		const domNodes = DomNodes.setElement(element.value) as DomNodesMedia;
		domNodes.setPositionInCropMode(ev.target, pos.x, pos.y);
	};

	const resizeHandler = (ev: OnResize) => {
		if (!element.value.hasCrop()) return;

		const { drag, target, width, height } = ev;
		if (width <= 0 || height <= 0) return;
		const { translate } = drag;

		const position = {
			x: translate[0],
			y: translate[1],
		};

		const prevSize =
			initialCropSize.value.width && initialCropSize.value.height
				? {
						width: initialCropSize.value.width,
						height: initialCropSize.value.height,
				  }
				: {
						width: initialSize.value.width,
						height: initialSize.value.height,
				  };
		const size = { width, height: width / (prevSize.width / prevSize.height) };

		store.$patch(() => {
			element.value.crop.size = size;
			element.value.crop.position = position;
		});

		// Es necesario para evitar flickeo al redimensionar
		const domNodes = DomNodes.setElement(element.value) as DomNodesMedia;
		domNodes.setSizeInCropMode(target, size.width, size.height);
		domNodes.setPositionInCropMode(target, position.x, position.y);
	};

	const unsetMoveableConfigForCrop = async () => {
		const selection = selectionId.value;
		await nextTick();
		clearSelection();
		await nextTick();
		selectionId.value = selection;
		// Si previamente teníamos el panel de imágenes abierto, lo recuperamos
		if (imagePanelWasOpen.value && !isMobile.value) {
			store.editPanel = EditPanels.Image;
			imagePanelWasOpen.value = false;
		}

		// En versión mobile restauramos el panel de imagen siempre después de un crop
		restoreImagePanelMobile();
	};

	const restoreImagePanelMobile = () => {
		if (store.editPanel !== EditPanels.Image && isMobile.value) {
			store.editPanel = EditPanels.Image;
		}
	};

	// #3615 | Crop limits
	// const getGhostLimits = (delta: number[]) => {
	// 	const crop = image.value.crop;
	// 	const position = image.value.position;
	// 	const size = image.value.size;
	// 	return {
	// 		top: crop.position.x + delta[1] >= 0,
	// 		right: crop.position.x + crop.size.width + delta[0] <= position.x + size.width,
	// 		bottom: crop.position.y + crop.size.height + delta[1] <= size.height,
	// 		left: crop.position.y + delta[0] >= 0,
	// 	}
	// }

	// Methods | PreCrop
	const preCropHandler = (delta: number[], dir: number[], size: Size) => {
		const isLeftHandler = dir[0] === -1;
		const isRightHandler = dir[0] === 1;
		const isTopHandler = dir[1] === -1;
		const isBottomHandler = dir[1] === 1;

		const gettingBiggerWidth = delta[0] > 0;
		const gettingSmallWidth = delta[0] < 0;
		const gettingBiggerHeight = delta[1] > 0;
		const gettingSmallHeight = delta[1] < 0;

		const horizontalLimitContract = temporalSize.value && temporalSize.value.width < element.value.crop.size.width;
		const verticalLimitContract = temporalSize.value && temporalSize.value.height < element.value.crop.size.height;

		if (!element.value.hasCrop()) {
			element.value.crop.size.width = element.value.size.width;
			element.value.crop.size.height = element.value.size.height;
		}

		const domNodes = DomNodes.setElement(element.value) as DomNodesMedia;

		const { cropSize, cropPosition } = domNodes.nodeData;

		if (isRightHandler) {
			const shouldExpandWidth = gettingBiggerWidth && cropSize.width - size.width + cropPosition.x <= 0;
			const shouldContractWidth = gettingSmallWidth && verticalLimitContract && horizontalLimitContract;

			if (!isCropping.value && (shouldExpandWidth || shouldContractWidth)) {
				setWidthAndKeepPosition(delta[0]);
			}
		}

		if (isLeftHandler) {
			const shouldExpandWidth = gettingBiggerWidth && cropPosition.x >= 0;
			const shouldContractWidth = gettingSmallWidth && horizontalLimitContract && verticalLimitContract;

			if (!isCropping.value && (shouldExpandWidth || shouldContractWidth)) {
				setWidthAndKeepPosition(delta[0], true);
			} else {
				domNodes.setPositionToCroppedNode((cropPosition.x += delta[0]), cropPosition.y);
			}
		}

		if (isBottomHandler) {
			const shouldExpandHeight = gettingBiggerHeight && cropSize.height - size.height + cropPosition.y <= 0;
			const shouldContractHeight = gettingSmallHeight && verticalLimitContract && horizontalLimitContract;
			if (!isCropping.value && (shouldExpandHeight || shouldContractHeight)) {
				setHeightAndKeepPosition(delta[1]);
			}
		}

		if (isTopHandler) {
			const shouldExpandHeight = gettingBiggerHeight && cropPosition.y >= 0;
			const shouldContractHeight = gettingSmallHeight && horizontalLimitContract && verticalLimitContract;

			if (!isCropping.value && (shouldExpandHeight || shouldContractHeight)) {
				setHeightAndKeepPosition(delta[1], true);
			} else {
				domNodes.setPositionToCroppedNode(cropPosition.x, (cropPosition.y += delta[1]));
			}
		}
	};

	const setWidthAndKeepPosition = (deltaX: number, resetTranslate = false) => {
		// const croppedImgNode = element.value.domNode()?.querySelector('.cropped-img');
		const baseImgNode = element.value.domNode();
		const domNodes = DomNodes.setElement(element.value) as DomNodesMedia;

		const { cropSize, cropPosition, originalSize } = domNodes.nodeData;

		if (!baseImgNode) return;
		const prevHeight = cropSize.height;
		// Actualizamos el size del crop al nuevo width manteniendo el aspect ratio
		const currentSize = {
			width: cropSize.width,
			height: cropSize.height,
		};

		let newCropSize = TransformTools.getSizeKeepingAspectRatioByWidth(
			currentSize,
			element.value.crop.size.width < element.value.size.width ? originalSize.width + deltaX : cropSize.width + deltaX
		);
		if (deltaX < 0 && newCropSize.height < element.value.size.height) {
			newCropSize = TransformTools.getSizeKeepingAspectRatioByHeight(newCropSize, originalSize.height);
		}
		const diffHeight = newCropSize.height - prevHeight;

		domNodes.setSizeToCroppedNode(newCropSize.width, newCropSize.height);

		// Cuando tiras de los handlers top o left y el movimiento es muy brusco se produce un offset en la caja
		// reseteamos el position para evitar este efecto
		if (resetTranslate && cropPosition.x > 0) {
			domNodes.setPositionToCroppedNode(0, cropPosition.y);
		}
		domNodes.setPositionToCroppedNode(cropPosition.x, (cropPosition.y -= diffHeight / 2));
	};

	const setHeightAndKeepPosition = (deltaY: number, resetTranslate = false) => {
		const domNodes = DomNodes.setElement(element.value) as DomNodesMedia;

		const { cropPosition, cropSize, originalSize } = domNodes.nodeData;

		const prevWidth = cropSize.width;
		// Actualizamos el size del crop al nuevo height manteniendo el aspect ratio
		let newCropSize = TransformTools.getSizeKeepingAspectRatioByHeight(
			element.value.crop.size,
			element.value.crop.size.height < element.value.size.height
				? originalSize.height + deltaY
				: cropSize.height + deltaY
		);
		if (deltaY < 0 && element.value.crop.size.width < element.value.size.width) {
			newCropSize = TransformTools.getSizeKeepingAspectRatioByWidth(newCropSize, originalSize.width);
		}
		const diffWidth = newCropSize.width - prevWidth;

		domNodes.setSizeToCroppedNode(newCropSize.width, newCropSize.height);
		// Cuando tiras de los handlers top o left y el movimiento es muy brusco se produce un offset en la caja
		// reseteamos el position para evitar este efecto
		if (resetTranslate && cropPosition.y > 0) {
			domNodes.setPositionToCroppedNode(cropPosition.y, 0);
		}
		domNodes.setPositionToCroppedNode((cropPosition.x -= diffWidth / 2), cropPosition.y);
	};

	const fitCroppedMediaOnResize = (element: CroppeableElement, size: Size) => {
		if (!element.hasCrop()) return;
		const domNodes = DomNodes.setElement(element) as DomNodesMedia;

		const { cropPosition, cropSize, originalSize } = domNodes.nodeData;
		const finalWidth = (cropSize.width *= size.width / originalSize.width);
		const finalHeight = (cropSize.height *= size.height / originalSize.height);
		domNodes.setSizeToCroppedNode(finalWidth, finalHeight);
		domNodes.setPositionToCroppedNode(
			(cropPosition.x *= size.width / originalSize.width),
			(cropPosition.y *= size.height / originalSize.height)
		);
	};

	const resizeCropByCornerHandler = (element: CroppeableElement, delta: number[], dir: number[]) => {
		if (!element.hasCrop()) return;

		const isLeftTopHandler = dir[0] === -1 && dir[1] === -1;
		const isRightTopHandler = dir[0] === 1 && dir[1] === -1;
		const isLeftBottomHandler = dir[0] === -1 && dir[1] === 1;
		const domNodes = DomNodes.setElement(element) as DomNodesMedia;

		const { cropPosition } = domNodes.nodeData;

		if (isLeftTopHandler) {
			domNodes.setPositionToCroppedNode((cropPosition.x += delta[0]), (cropPosition.y += delta[1]));
		}

		if (isLeftBottomHandler) {
			domNodes.setPositionToCroppedNode((cropPosition.x += delta[0]), cropPosition.y);
		}

		if (isRightTopHandler) {
			domNodes.setPositionToCroppedNode(cropPosition.x, (cropPosition.y += delta[1]));
		}
	};

	const resizeCropByMiddleHandler = (element: CroppeableElement, delta: number[], dir: number[]) => {
		if (!element.hasCrop()) return;
		const isLeftHandler = dir[0] === -1;
		const isTopHandler = dir[1] === -1;
		const domNodes = DomNodes.setElement(element) as DomNodesMedia;

		const { cropPosition } = domNodes.nodeData;

		if (isLeftHandler) {
			// #3615 | Crop limits
			// element.crop.position.x = limits.left && delta[0] >= 0 ? 0 : element.crop.position.x + delta[0];
			domNodes.setPositionToCroppedNode(cropPosition.x + delta[0], cropPosition.y);
		}

		if (isTopHandler) {
			// #3615 | Crop limits
			// element.crop.position.y = limits.top && delta[1] >= 0 ? 0 : element.crop.position.y + delta[1];
			domNodes.setPositionToCroppedNode(cropPosition.x, cropPosition.y + delta[1]);
		}
	};

	return {
		initCrop,
		applyCrop,
		cancelCrop,
		preCropHandler,
		fitCroppedMediaOnResize,
		resizeCropByCornerHandler,
		resizeCropByMiddleHandler,
		initialCropPosition,
		initialCropSize,
		initialPosition,
		initialSize,
		fromApplyCrop,
		temporalSize,
	};
};

export const useGhostMoveable = () => ({ ghostMoveable });
